'use client'

import { useState } from 'react'
import { Button, Column, Grid, Link, Tile } from '@carbon/react'
import { Checkmark, Copy, Launch, LogoGithub } from '@carbon/react/icons'
import type { IntegrationsCopy } from '@/lib/integrations-i18n'

const integrations = [
  {
    id: 'mcp',
    name: 'MCP',
    descriptionKey: 'mcpDescription',
    bestForKey: 'mcpBestFor',
    logos: [
      ['/developer-tools/claude.svg', 'Claude', false],
      ['/developer-tools/openai.svg', 'ChatGPT', true],
      ['/developer-tools/gemini.svg', 'Gemini', false],
      ['/developer-tools/cursor.svg', 'Cursor', true],
      ['/developer-tools/mcp.svg', 'Others', true],
    ],
    quickStart: 'https://mcp.kmerhosting.com/mcp',
    github: 'https://github.com/KmerHosting/mcp',
    docs: 'https://kmerhosting.com/docs/developer/mcp',
    service: 'https://mcp.kmerhosting.com/mcp',
  },
  {
    id: 'sdk',
    name: 'SDK',
    descriptionKey: 'sdkDescription',
    bestForKey: 'sdkBestFor',
    logos: [
      ['/developer-tools/typescript.svg', 'TypeScript', false],
      ['/developer-tools/python.svg', 'Python', false],
      ['/developer-tools/php.svg', 'PHP', false],
      ['/developer-tools/java.svg', 'Java', false],
    ],
    quickStart: 'npm install @kmerhosting/sdk\nexport KMERHOSTING_API_KEY="kh_live_..."',
    github: 'https://github.com/KmerHosting/sdk',
    docs: 'https://kmerhosting.com/docs/developer/sdk',
  },
  {
    id: 'api',
    name: 'REST API',
    descriptionKey: 'apiDescription',
    bestForKey: 'apiBestFor',
    logos: [['/developer-tools/openapi.svg', 'OpenAPI', true]],
    quickStart: 'curl https://api.kmerhosting.com/v1/services \\\n  -H "Authorization: Bearer $KMERHOSTING_API_KEY"',
    github: 'https://github.com/KmerHosting/api',
    docs: 'https://kmerhosting.com/docs/developer/rest-api',
    service: 'https://api.kmerhosting.com/docs',
  },
  {
    id: 'cli',
    name: 'CLI',
    descriptionKey: 'cliDescription',
    bestForKey: 'cliBestFor',
    logos: [['/developer-tools/bun.svg', 'Bun', false]],
    quickStart: 'bun add --global github:KmerHosting/cli\nexport KMERHOSTING_API_KEY="kh_live_..."\nkmerhosting services list',
    github: 'https://github.com/KmerHosting/cli',
    docs: 'https://kmerhosting.com/docs/developer/cli',
  },
] as const

export function IntegrationsClient({ copy }: { copy: IntegrationsCopy }) {
  const [copiedId, setCopiedId] = useState<string | null>(null)

  const copyQuickStart = async (id: string, value: string) => {
    await navigator.clipboard.writeText(value)
    setCopiedId(id)
    window.setTimeout(() => setCopiedId((current) => current === id ? null : current), 1600)
  }

  return (
    <main id="main-content" className="page-main">
      <section className="catalog-detail__hero integrations-hero" aria-labelledby="integrations-title">
        <Grid>
          <Column sm={4} md={7} lg={11}>
            <h1 id="integrations-title" className="catalog-detail__title">{copy.title}</h1>
            <p className="catalog-detail__subtitle">{copy.intro}</p>
            <div className="hero__actions">
              <Button href="https://kmerhosting.com/docs/developer/overview" target="_blank" rel="noreferrer" renderIcon={Launch}>
                {copy.documentation}
              </Button>
              <Button href="https://dash.kmerhosting.com/?view=api-keys" target="_blank" rel="noreferrer" kind="tertiary">
                {copy.securityTitle}
              </Button>
            </div>
          </Column>
        </Grid>
      </section>

      <section className="section integrations-section" aria-labelledby="integration-options-title">
        <Grid>
          <Column sm={4} md={8} lg={16}>
            <div className="integrations-section__intro">
              <h2 id="integration-options-title" className="section__title">{copy.sectionTitle}</h2>
              <p className="section__subtitle">{copy.sectionIntro}</p>
            </div>

            <div className="integrations-grid">
              {integrations.map((integration) => {
                const description = copy[integration.descriptionKey]
                const bestFor = copy[integration.bestForKey]

                return (
                  <Tile key={integration.id} className="integration-card">
                    <div className="integration-card__heading">
                      <div className="integration-card__brand">
                        <img src="/brand/logo-blue-transparent.png" alt="" width="44" height="44" />
                      </div>
                      <div>
                        <h3>{integration.name}</h3>
                        <p>{description}</p>
                      </div>
                    </div>

                    <div className="integration-card__logos" aria-label={integration.name}>
                      {integration.logos.map(([src, label, mono]) => (
                        <span className="integration-logo" key={label}>
                          <img
                            className={mono ? 'integration-logo__image integration-logo__image--mono' : 'integration-logo__image'}
                            src={src}
                            alt=""
                            width="26"
                            height="26"
                          />
                          <span>{label}</span>
                        </span>
                      ))}
                    </div>

                    <div className="integration-card__best-for">
                      <span>{copy.bestFor}</span>
                      <p>{bestFor}</p>
                    </div>

                    <div className="integration-card__quick-start">
                      <div className="integration-card__quick-start-heading">
                        <span>{copy.quickStart}</span>
                        <Button
                          kind="ghost"
                          size="sm"
                          renderIcon={copiedId === integration.id ? Checkmark : Copy}
                          onClick={() => void copyQuickStart(integration.id, integration.quickStart)}
                        >
                          {copiedId === integration.id ? copy.copied : copy.copyCode}
                        </Button>
                      </div>
                      <pre><code>{integration.quickStart}</code></pre>
                    </div>

                    <div className="integration-card__actions">
                      <Button href={integration.github} target="_blank" rel="noreferrer" kind="secondary" size="sm" renderIcon={LogoGithub}>
                        {copy.sourceCode}
                      </Button>
                      <Button href={integration.docs} target="_blank" rel="noreferrer" kind="tertiary" size="sm" renderIcon={Launch}>
                        {copy.documentation}
                      </Button>
                      {'service' in integration && integration.service ? (
                        <Link href={integration.service} target="_blank" rel="noreferrer">{copy.openService}</Link>
                      ) : null}
                    </div>
                  </Tile>
                )
              })}
            </div>
          </Column>
        </Grid>
      </section>

      <section className="section products-section integrations-security" aria-labelledby="integrations-security-title">
        <Grid>
          <Column sm={4} md={5} lg={7}>
            <h2 id="integrations-security-title" className="section__title">{copy.securityTitle}</h2>
            <p className="section__subtitle">{copy.securityBody}</p>
            <Link href="https://kmerhosting.com/docs/developer/api-keys" target="_blank" rel="noreferrer">{copy.documentation}</Link>
          </Column>
        </Grid>
      </section>
    </main>
  )
}
